import { doc, collection, query, where, getDocs, getDoc, setDoc, increment } from 'firebase/firestore';
import { db } from '../firebase';
import { askGPT } from '../components/OpenAIService';
import { updateQuizProgress } from '../components/progressManager';

/**
 * Service pour gérer les quiz par concept
 * Adapté à la structure de données existante et intégrant les fonctions communes
 */
const ConceptQuizServices = {
  /**
   * Récupère un quiz existant pour un concept
   * @param {string} userId - ID de l'utilisateur
   * @param {string} competenceId - ID de la compétence (non utilisé dans le chemin, mais conservé pour la cohérence)
   * @param {string} conceptId - ID du concept au format: nom_concept*D*_document_id
   * @returns {Promise<Object|null>} - Quiz existant ou null
   */
  getExistingQuiz: async (userId, competenceId, conceptId) => {
    try {
      // Structure: /users/{userId}/quizzes/{conceptId}
      console.log(`Recherche du quiz: /users/${userId}/quizzes/${conceptId}`);
      const quizRef = doc(db, "users", userId, "quizzes", conceptId);
      const quizSnapshot = await getDoc(quizRef);
      
      if (quizSnapshot.exists()) {
        console.log("Quiz trouvé dans Firestore");
        return quizSnapshot.data();
      }
      console.log("Aucun quiz trouvé pour ce concept");
      return null;
    } catch (error) {
      console.error("Erreur lors de la récupération du quiz:", error);
      return null;
    }
  },
  
  /**
   * Sauvegarde un quiz pour un concept
   * @param {string} userId - ID de l'utilisateur
   * @param {string} competenceId - ID de la compétence
   * @param {string} conceptId - ID du concept au format: nom_concept*D*_document_id
   * @param {Array} questions - Questions du quiz
   * @param {number} currentIndex - Index de la question actuelle
   * @param {boolean} completed - Quiz terminé ou non
   * @returns {Promise<boolean>} - Succès ou échec
   */
  saveQuiz: async (userId, competenceId, conceptId, questions, currentIndex, completed) => {
    try {
      // Structure: /users/{userId}/quizzes/{conceptId}
      console.log(`Sauvegarde du quiz: /users/${userId}/quizzes/${conceptId}`);
      const quizRef = doc(db, "users", userId, "quizzes", conceptId);
      
      const quizData = {
        userId,
        competenceId,   // Garder cette référence pour pouvoir filtrer par compétence
        conceptId,      // Stocker l'ID du concept pour référence
        questions,
        currentQuestionIndex: currentIndex,
        completed,
        updatedAt: new Date().toISOString()
      };
      
      await setDoc(quizRef, quizData, { merge: true });
      console.log("Quiz sauvegardé avec succès");
      return true;
    } catch (error) {
      console.error("Erreur lors de la sauvegarde du quiz:", error);
      throw error;
    }
  },

  /**
   * Met à jour la progression du quiz et de la compétence
   * @param {string} userId - ID de l'utilisateur
   * @param {string} competenceId - ID de la compétence
   * @param {string} conceptId - ID du concept au format: nom_concept*D*_document_id
   * @param {boolean} isCorrect - Réponse correcte ou non
   * @param {boolean} isQuizCompleted - Quiz terminé ou non
   * @returns {Promise<Object>} - Données de progression mises à jour
   */
  async updateProgress(userId, competenceId, conceptId, isCorrect, isQuizCompleted = false) {
    if (!userId || !competenceId || !conceptId) {
      throw new Error("Paramètres invalides pour la mise à jour de la progression");
    }
    
    try {
      // 1. Mettre à jour la progression du quiz de concept
      // Structure: /users/{userId}/quizzes/{conceptId}
      console.log(`Mise à jour de la progression: /users/${userId}/quizzes/${conceptId}`);
      const quizRef = doc(db, "users", userId, "quizzes", conceptId);
      let currentProgress = {
        totalCorrectAnswers: 0,
        totalQuestionsAnswered: 0,
        quizCompleted: false,
        lastQuizDate: null
      };
      
      try {
        const quizSnap = await getDoc(quizRef);
        if (quizSnap.exists() && quizSnap.data().progress) {
          currentProgress = quizSnap.data().progress;
        }
      } catch (error) {
        console.warn("Erreur lors de la récupération de la progression existante:", error);
      }
      
      const newProgress = {
        totalCorrectAnswers: isCorrect 
          ? (currentProgress.totalCorrectAnswers || 0) + 1 
          : (currentProgress.totalCorrectAnswers || 0),
        totalQuestionsAnswered: (currentProgress.totalQuestionsAnswered || 0) + 1,
        quizCompleted: isQuizCompleted || currentProgress.quizCompleted || false,
        lastQuizDate: new Date().toISOString()
      };
      
      // Mettre à jour la progression dans le document du quiz
      await setDoc(quizRef, {
        progress: newProgress,
        completed: isQuizCompleted,
        updatedAt: new Date().toISOString()
      }, { merge: true });
      console.log("Progression du quiz mise à jour avec succès");
      
      // 2. Mettre à jour la progression globale de la compétence en utilisant updateQuizProgress
      try {
        // Récupérer d'abord la progression actuelle de la compétence pour obtenir les valeurs totales
        const competenceProgressRef = doc(db, "users", userId, "competenceProgress", competenceId);
        const competenceProgressSnap = await getDoc(competenceProgressRef);
        
        let currentTotalCorrectAnswers = 0;
        let currentTotalQuestionsAnswered = 0;
        
        if (competenceProgressSnap.exists()) {
          currentTotalCorrectAnswers = competenceProgressSnap.data().totalCorrectAnswers || 0;
          currentTotalQuestionsAnswered = competenceProgressSnap.data().totalQuestionsAnswered || 0;
        }
        
        // Calculer les nouvelles valeurs totales
        const newTotalCorrectAnswers = isCorrect 
          ? currentTotalCorrectAnswers + 1 
          : currentTotalCorrectAnswers;
          
        const newTotalQuestionsAnswered = currentTotalQuestionsAnswered + 1;
        
        // Utiliser updateQuizProgress de ProgressManager.js pour mettre à jour la progression
        // Cette fonction mettra également à jour le champ progress automatiquement
        await updateQuizProgress(
          userId, 
          competenceId, 
          newTotalCorrectAnswers, 
          newTotalQuestionsAnswered
        );
        
        // Si le quiz est complété, mettre à jour manuellement ce champ spécifique
        if (isQuizCompleted) {
          await setDoc(competenceProgressRef, {
            quizCompleted: true,
            lastUpdated: new Date().toISOString()
          }, { merge: true });
        }
        
        console.log("Progression de la compétence mise à jour avec succès via updateQuizProgress");
      } catch (error) {
        console.warn("Erreur lors de la mise à jour de la progression de la compétence:", error);
      }
      
      return newProgress;
    } catch (error) {
      console.error("Erreur lors de la mise à jour de la progression:", error);
      return {
        totalCorrectAnswers: 0,
        totalQuestionsAnswered: 0,
        quizCompleted: false,
        lastQuizDate: new Date().toISOString()
      };
    }
  },
  
  /**
   * Génère un quiz pour un concept en utilisant askGPT
   * Utilise strictement les champs concept et description
   * @param {Object} conceptData - Données du concept (avec concept et description)
   * @param {number} questionCount - Nombre de questions à générer
   * @returns {Promise<Array>} - Questions générées
   */
  async generateQuizQuestions(conceptData, questionCount = 5) {
    if (!conceptData) {
      throw new Error("Données du concept manquantes");
    }
    
    // Vérification du champ concept (titre)
    if (!conceptData.concept) {
      throw new Error("Titre du concept (champ 'concept') manquant");
    }
    
    try {
      // Utiliser directement le champ description tel qu'il est
      const description = conceptData.description.description || "";
      const resume = conceptData.description.resume || "";
      console.log("Appel de generateQuizQuestions");
      // Structurer le prompt pour l'IA avec le titre et la description uniquement
      const prompt = `Génère un quiz de ${questionCount} questions pour évaluer la compréhension du concept: "${conceptData.concept}"
                     ${description} ${resume} 
                     
                     Format JSON strict pour la réponse: 
                     {"questions": [
                       {
                         "question": "Question 1?", 
                         "options": ["Option A", "Option B", "Option C", "Option D"], 
                         "answer": "Option correcte exactement comme écrite dans options", 
                         "feedbackCorrect": "Explication pour réponse correcte", 
                         "feedbackIncorrect": "Explication pour réponse incorrecte"
                       },
                       ...
                     ]}
                     
                     Règles importantes:
                     1. Les questions doivent être précises et directement liées au concept.
                     2. Les options doivent être plausibles mais une seule doit être correcte.
                     3. La valeur de "answer" doit correspondre EXACTEMENT à l'une des options.
                     4. Utilise $...$ pour les formules mathématiques en ligne et $$...$$ pour les formules en mode affiché.
                     5. Assure-toi que le JSON est valide et bien formaté.`;
      
      console.log("Envoi du prompt pour génération de quiz:", {
        concept: conceptData.concept,
        descriptionLength: description ? description.length : 0
      });
      
      // Utiliser askGPT pour générer les questions
      const response = await askGPT(prompt);
      
      // Extraire le JSON de la réponse
      const jsonStartIndex = response.indexOf("{");
      const jsonEndIndex = response.lastIndexOf("}") + 1;
      
      if (jsonStartIndex === -1 || jsonEndIndex === -1) {
        console.error("Réponse JSON invalide:", response);
        throw new Error("Réponse JSON invalide");
      }
      
      let jsonString = response.slice(jsonStartIndex, jsonEndIndex);
      
      // Préparation pour parsing JSON
      try {
        // Parser le JSON
        const parsedResponse = JSON.parse(jsonString);
        
        if (!parsedResponse.questions || !Array.isArray(parsedResponse.questions)) {
          throw new Error("Format de réponse invalide: pas de tableau de questions");
        }
        
        // Vérifier et valider chaque question
        const validatedQuestions = parsedResponse.questions.map(q => {
          // Vérifier que la réponse est dans les options
          if (!q.options.includes(q.answer)) {
            console.warn(`Question avec réponse invalide: "${q.question}". Réponse "${q.answer}" non trouvée dans les options.`);
            // Correction: utiliser la première option comme réponse par défaut
            q.answer = q.options[0];
          }
          return q;
        });
        
        console.log(`Quiz généré avec succès: ${validatedQuestions.length} questions`);
        return validatedQuestions;
      } catch (parseError) {
        console.error("Erreur de parsing JSON:", parseError, "Contenu:", jsonString);
        throw new Error(`Erreur de parsing JSON: ${parseError.message}`);
      }
    } catch (error) {
      console.error("Erreur lors de la génération du quiz:", error);
      throw error;
    }
  }
};

export default ConceptQuizServices;